import React, { useState, useEffect } from "react";
import {
  Form,
  Row,
  Col,
  Skeleton,
  Select,
  Input,
  Button,
  Modal,
  InputNumber,
} from "antd";
import { IdcardOutlined, CalendarOutlined } from "@ant-design/icons";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import DatePicker from "@general/datePicker";
import moment from "moment-jalaali";

function AddNewDiscount({ onDataUpdate }) {
  const CREATEDISCOUNT = gql`
    mutation createDiscount($createInput: CreateDiscountInput!) {
      createDiscount(createInput: $createInput) {
        id
      }
    }
  `;
  const GETUSERSANDPRODUCTS = gql`
  query (
    $paginateInput: PaginateInput!
    $userSearchInput: UserSearchInput!
    $productSearchInput: ProductSearchInput!
  ) {
    getProducts(
      paginateInput: $paginateInput
      searchInput: $productSearchInput
    ) {
      id
      name
      category
    }

    getUsers(
      paginateInput: $paginateInput
      searchInput: $userSearchInput
    ) {
      id
      name
      mobile
    }
  }
`;
  const [selectableProducts, setSelectableProducts] = useState([]);
  const [selectableUsers, setSelectableUsers] = useState([]);
  const [error, setError] = useState(null);
  const [getUsersAndProducts, usersAndProducts] = useLazyQuery(
    GETUSERSANDPRODUCTS,
    {
      onCompleted({ getProducts, getUsers }) {
        const arrangedProducts = [
          { value: "all", label: "همه خدمات", title: "همه خدمات" },
          {
            value: "000000000000000000000000",
            label: (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#e6f7ff",
                  color: "#096dd9",
                  padding: "3px 8px",
                  borderRadius: 4,
                  fontWeight: 500,
                }}
              >
                <IdcardOutlined style={{ marginLeft: 8, fontSize: 16, color: "#1890ff" }} />
                <span>کارت عضویت</span>
              </span>
            ),
            title: "کارت عضویت",
          },
          {
            value: "000000000000000000000002",
            label: (
              <div style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 6 }}>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#f9f0ff",
                    color: "#531dab",
                    padding: "3px 8px",
                    borderRadius: 4,
                    fontWeight: 500,
                  }}
                >
                  <CalendarOutlined style={{ marginLeft: 8, fontSize: 16, color: "#722ed1" }} />
                  <span>روزهای تعطیل</span>
                </span>
              </div>
            ),
            title: "روزهای تعطیل",
          },
        ];

        const CATEGORIES = [
          { key: "WORK_SPACE", title: "فضای کار" },
          { key: "MEETING_ROOM", title: "اتاق جلسات" },
          { key: "CLASS", title: "دوره‌های آموزشی" },
          { key: "EVENT", title: "رویدادها" },
          { key: "CONSULTATION", title: "مشاوره" },
        ];

        const groupedCategoryOptions = [];

        CATEGORIES.forEach(({ key, title }) => {
          const categoryProducts = (getProducts || [])
            .filter((p) => p.category === key)
            .map((p) => ({
              value: p.id,
              label: p.name ? p.name : p.mobile,
              title: p.name ? p.name : p.mobile,
            }))
            .sort((a, b) => (a.title || "").localeCompare(b.title || "", "fa"));

          if (categoryProducts.length > 0) {
            groupedCategoryOptions.push({
              label: (
                <span
                  style={{
                    color: "#8c8c8c",
                    fontWeight: 600,
                    fontSize: "12px",
                    display: "block",
                    padding: "4px 0",
                  }}
                >
                  {title}
                </span>
              ),
              title,
              options: categoryProducts,
            });
          }
        });

        const categorizedIds = new Set(
          (getProducts || [])
            .filter((p) => CATEGORIES.some((c) => c.key === p.category))
            .map((p) => p.id)
        );
        const uncategorized = (getProducts || [])
          .filter((p) => !categorizedIds.has(p.id))
          .map((p) => ({
            value: p.id,
            label: p.name ? p.name : p.mobile,
            title: p.name ? p.name : p.mobile,
          }))
          .sort((a, b) => (a.title || "").localeCompare(b.title || "", "fa"));

        if (uncategorized.length > 0) {
          groupedCategoryOptions.push({
            label: (
              <span
                style={{
                  color: "#8c8c8c",
                  fontWeight: 600,
                  fontSize: "12px",
                  display: "block",
                  padding: "4px 0",
                }}
              >
                سایر خدمات
              </span>
            ),
            title: "سایر خدمات",
            options: uncategorized,
          });
        }

        setSelectableProducts([...arrangedProducts, ...groupedCategoryOptions]);

        const arrangedUsers = [{ value: "all", label: "همه کاربران" }];
        getUsers.map((user) => {
          arrangedUsers.push({
            value: user.id,
            label: user.name ? user.name : user.mobile,
          });
        });
        setSelectableUsers(arrangedUsers);
      },
    }
  );

  const [form] = Form.useForm();
  const [runCreateDiscount, createDiscount] = useMutation(CREATEDISCOUNT, {
    onCompleted(data) {
      Modal.success({
        title: "موفق",
        content: "تخفیف ثبت شد",
        bodyStyle: { direction: "rtl" },
        okText: "باشه",
        onOk: () => form.resetFields(),
      });
      onDataUpdate();
    },
    onError(err) {
      console.log("خطا: " + err.message);
      Modal.error({
        title: "مشکلی پیش آمده!",
        content: err.message,
        bodyStyle: { direction: "rtl" },
        okText: "باشه",
      });
    },
  });

  useEffect(() => {
    getUsersAndProducts({
      variables: {
        paginateInput: {
          page: 1,
          perPage: 500,
        },
        productSearchInput: {},
        userSearchInput: {
          userRole: "USER",
        },
      },
    });
  }, []);

  const doCreateDiscount = (createInput) => {
    if (createInput.userIds[0] === "all") createInput.userIds = [];
    if (createInput.productIds[0] === "all") createInput.productIds = [];
    runCreateDiscount({
      variables: {
        createInput: {
          ...createInput,
        },
      },
    });
  };
  if (usersAndProducts.loading)
    return (
      <>
        <Skeleton active round />
        <br />
        <Skeleton active round />
      </>
    );

  return (
    <>
      <Form layout="vertical" onFinish={doCreateDiscount} form={form}>
        <Row>
          <Col xs={24} md={12}>
            <Row>
              <Form.Item
                name="title"
                id="title"
                label="عنوان"
                rules={[
                  {
                    required: true,
                    message: "لطفا کد تخفیف را وارد کنید!",
                  },
                ]}
                validateStatus={error && "error"}
              >
                <Input className="form-input" placeholder={"عنوان"} />
              </Form.Item>
            </Row>
            <Row>
              <Form.Item
                name="productIds"
                id="productIds"
                label="خدمات مشمول"
                rules={[
                  {
                    required: true,
                    message: "لطفا نام خدمات مشمول تخفیف را انتخاب کنید!",
                  },
                ]}
                validateStatus={error && "error"}
              >
                <Select
                  showSearch
                  mode="multiple"
                  allowClear
                  loading={usersAndProducts.loading}
                  listHeight={200}
                  style={{
                    width: 280,
                  }}
                  placeholder={"خدمات"}
                  optionFilterProp="title"
                  filterOption={(input, option) => {
                    const searchText = option?.title || (typeof option?.label === "string" ? option.label : "") || "";
                    return searchText.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={selectableProducts}
                />
              </Form.Item>
            </Row>
            <Row>
              <Form.Item
                name="percent"
                id="percent"
                label="درصد تخفیف"
                rules={[
                  {
                    required: true,
                    message: "لطفا درصد تخفیف را وارد کنید!",
                  },
                ]}
                validateStatus={error && "error"}
              >
                <InputNumber
                  min={0}
                  max={100}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace("%", "")}
                />
              </Form.Item>
            </Row>

            <Row>
              <Form.Item
                name="maxCount"
                id="maxCount"
                label="تعداد"
                rules={[
                  {
                    required: true,
                    message: "لطفا حداکثر تعداد استفاده را مشخص کنید!",
                  },
                ]}
                validateStatus={error && "error"}
              >
                <InputNumber min={1} style={{ width: "150px" }} />
              </Form.Item>
            </Row>
          </Col>
          <Col xs={24} md={12}>
            <Row>
              <Form.Item
                name="code"
                id="code"
                label="کد تخفیف"
                rules={[
                  {
                    required: true,
                    message: "لطفا کد تخفیف را وارد کنید!",
                  },
                ]}
                validateStatus={error && "error"}
              >
                <Input className="form-input" placeholder={"کد تخفیف"} />
              </Form.Item>
            </Row>
            <Row>
              <Form.Item
                name="userIds"
                id="userIds"
                label="کاربران مشمول"
                rules={[
                  {
                    required: true,
                    message: "لطفا نام کاربران مشمول تخفیف را انتخاب کنید!",
                  },
                ]}
                validateStatus={error && "error"}
              >
                <Select
                  showSearch
                  mode="multiple"
                  allowClear
                  loading={usersAndProducts.loading}
                  listHeight={200}
                  style={{
                    width: 280,
                  }}
                  placeholder={"کاربران"}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? "").includes(input)
                  }
                  filterSort={(optionB, optionA) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={selectableUsers}
                />
              </Form.Item>
            </Row>

            <Row>
              <Col span={24}>
                <Form.Item
                  label="تاریخ انقضا"
                  name="expiresAt"
                  rules={[{ required: true, message: "این فیلد ضروری است" }]}
                >
                  <DatePicker
                    showToday={false}
                    format="YYYY/MM/DD"
                    className="rtl"
                    placeholder="تاریخ انقضا"
                    disabledDate={(current) => current < moment().endOf("day")}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
        <Button
          type="primary"
          htmlType="submit"
          loading={createDiscount.loading && true}
        >
          ثبت
        </Button>
      </Form>
    </>
  );
}

export default AddNewDiscount;
